import { generateWarrantyWorkDocument } from '@/api/service'
import { nanoid } from 'nanoid'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import ExpandablePanel from '@/components/ui/ExpandablePanel.vue'
import FeatureTextarea from '@/components/cars/edit/FeatureTextarea.vue'
import DateHelpers from '@/mixins/DateHelpers'

// eslint-disable-next-line import/prefer-default-export
export const WarrantyOrderDocumentMixin = {
  components: {
    'mia-expandable-panel': ExpandablePanel,
    'mia-feature-input': FeatureInput,
    'mia-feature-textarea': FeatureTextarea
  },

  mixins: [DateHelpers],

  props: {
    make: {
      type: String,
      required: true
    },

    orderId: {
      type: String,
      default: ''
    },

    timeTrackings: {
      type: Array,
      default() {
        return []
      }
    }
  },

  data: () => ({
    breakdownIsUntouched: true
  }),

  watch: {
    orderId: {
      immediate: true,
      handler(currentOrderId, previousOrderId) {
        this.$set(this.form, 'orderId', this.orderId)

        if (!!previousOrderId && currentOrderId !== previousOrderId && this.confirmOverwritingBreakdown()) {
          this.setBreakdownFromTimeTrackings()
          this.breakdownIsUntouched = true
        }
      }
    },

    timeTrackings: {
      immediate: true,
      handler() {
        if (this.breakdownIsUntouched) {
          this.setBreakdownFromTimeTrackings()
        }
      }
    }
  },

  created() {
    if (this.form.breakdown.length === 0) {
      this.addBreakdownEntry(this.createEmptyBreakdownEntry())
    }
  },

  mounted() {
    this.$emit('mounted')
  },

  methods: {
    onDamageLocationChange(damageLocation, event) {
      let damageLocations = [...this.form.damageLocations].filter(damageLocationId => damageLocationId !== damageLocation.id)

      if (event.target.checked) {
        damageLocations = [...damageLocations, damageLocation.id]
      }

      this.$set(this.form, 'damageLocations', damageLocations)
    },

    onDamageTypeChange(damageType, event) {
      let damageTypes = [...this.form.damageTypes].filter(damageTypeId => damageTypeId !== damageType.id)

      if (event.target.checked) {
        damageTypes = [...damageTypes, damageType.id]
      }

      this.$set(this.form, 'damageTypes', damageTypes)
    },

    onDocumentChange(documentId, event) {
      let documents = [...this.form.documents].filter(existingDocumentId => existingDocumentId !== documentId)

      if (event.target.checked) {
        documents = [...documents, documentId]
      }

      this.$set(this.form, 'documents', documents)
    },

    updateBreakdownItemById(itemId, updates) {
      this.$set(this.form, 'breakdown', [...this.form.breakdown].map((existingItem) => {
        if (existingItem.id === itemId) {
          return {
            ...existingItem,
            ...updates
          }
        }

        return existingItem
      }))
    },

    onBreakdownDescriptionInput(item, description) {
      this.updateBreakdownItemById(item.id, { description })
      this.breakdownIsUntouched = false
    },

    onBreakdownMechanicIdInput(item, mechanicId) {
      this.updateBreakdownItemById(item.id, { mechanicId })
      this.breakdownIsUntouched = false
    },

    onBreakdownTimeTrackingInput(item, timeTracking) {
      this.updateBreakdownItemById(item.id, { timeTracking })
      this.breakdownIsUntouched = false
    },

    onBreakdownTimeTrackingBlur(item) {
      const isLast = this.form.breakdown.findIndex(existingItem => existingItem.id === item.id) === this.form.breakdown.length - 1

      if (!isLast) {
        return
      }

      if (!item.description && !item.mechanicId && !item.timeTracking) {
        return
      }

      this.addBreakdownEntry(this.createEmptyBreakdownEntry())
      this.$nextTick(() => {
        const lastItem = this.form.breakdown[this.form.breakdown.length - 1]
        const inputRef = this.$refs[`breakdownDescriptionRef${lastItem.id}`]

        if (inputRef) {
          inputRef.focus()
        }
      })
    },

    addBreakdownEntry(entry) {
      this.$set(this.form, 'breakdown', [...this.form.breakdown, entry])
    },

    createEmptyBreakdownEntry() {
      return {
        id: nanoid(),
        description: '',
        mechanicId: this.authUser.item_zls_id || '',
        timeTracking: ''
      }
    },

    focusInputRef(refName) {
      this.$nextTick(() => {
        if (this.$refs[refName]) {
          this.$refs[refName].focus()
        }
      })
    },

    onBreakdownPanelExpanded() {
      const lastItem = this.form.breakdown[this.form.breakdown.length - 1]

      this.focusInputRef(`breakdownDescriptionRef${lastItem.id}`)
    },

    onInternalPanelExpanded() {
      this.focusInputRef('internalCommentInputRef')
    },

    removeBreakdownItem(item) {
      this.$set(this.form, 'breakdown', [...this.form.breakdown].filter(existingItem => existingItem.id !== item.id))

      if (this.form.breakdown.length === 0) {
        this.addBreakdownEntry(this.createEmptyBreakdownEntry())
      }
    },

    confirmOverwritingBreakdown() {
      // eslint-disable-next-line no-alert
      return window.confirm('Die Auftragsnummer hat sich geändert. Sollen die durchgeführten Arbeiten anhand der Zeiterfassung überschrieben werden?')
    },

    setBreakdownFromTimeTrackings() {
      this.$set(this.form, 'breakdown', [])

      this.timeTrackings.forEach((timeTracking) => {
        this.addBreakdownEntry({
          ...this.createEmptyBreakdownEntry(),
          description: timeTracking.item_comment || '',
          timeTracking: timeTracking.item_hours_sold || ''
        })
      })

      if (this.form.breakdown.length === 0) {
        this.addBreakdownEntry(this.createEmptyBreakdownEntry())
      }
    },

    async submit(callback) {
      const [error, response] = await generateWarrantyWorkDocument(this.make, this.getSubmittableData())

      if (error) {
        callback(false, error)
        return
      }

      callback(true, response)
    },

    restore(data) {
      Object.keys(this.form).forEach((key) => {
        if (data[key] !== undefined) {
          this.$set(this.form, key, data[key])
        }
      })
    },

    getData() {
      return {
        ...this.form
      }
    }
  }
}
